import { useState, useEffect, useRef } from "react";
import ImageEditor from "./imageEditor/TuiEditor.js";
import ColorPicker from "react-best-gradient-color-picker";
import Cropper from "./cropper/Cropper.tsx";
import Button from "@mui/material/Button";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import Col from "react-bootstrap/Col";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

function Thumbnails() {
  const [selectedColor, setSelectedColor] = useState("linear-gradient(180deg, rgba(96,93,93,0) 0%, rgba(255,255,255,0) 100%)");

  const [editorHeight, setEditorHeight] = useState(window.innerHeight);
  const [image, setImage] = useState(null);
  const [imageCropped, setImageCropped] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [step, setStep] = useState(1);
  const cropperRef = useRef();

  const handleImageProcess = (image) => {
    setImage(image);
    if (step === 1) {
      setImageCropped(image);
    }

    // if (step === 1) setStep(2);
    // else if (step === 2) setStep(3);
    setStep(step + 1);
  };

  var whiteTheme = {
    "common.bi.image": "https://uicdn.toast.com/toastui/img/tui-image-editor-bi.png",
    "common.bisize.width": "251px",
    "common.bisize.height": "21px",
    "common.backgroundImage": "./img/bg.png",
    "common.backgroundColor": "#fff",
    "common.border": "1px solid #c1c1c1",

    // header
    "header.backgroundImage": "none",
    "header.backgroundColor": "transparent",
    "header.border": "0px",
    "header.display": "none",

    // load button
    "loadButton.backgroundColor": "#fff",
    "loadButton.border": "1px solid #ddd",
    "loadButton.color": "#222",
    "loadButton.fontFamily": "'Noto Sans', sans-serif",
    "loadButton.fontSize": "12px",

    // download button
    "downloadButton.backgroundColor": "#fdba3b",
    "downloadButton.border": "1px solid #fdba3b",
    "downloadButton.color": "#fff",
    "downloadButton.fontFamily": "'Noto Sans', sans-serif",
    "downloadButton.fontSize": "12px",

    // main icons
    "menu.normalIcon.color": "#8a8a8a",
    "menu.activeIcon.color": "#555555",
    "menu.disabledIcon.color": "#434343",
    "menu.hoverIcon.color": "#e9e9e9",
    "menu.iconSize.width": "24px",
    "menu.iconSize.height": "24px",

    // submenu icons
    "submenu.normalIcon.color": "#8a8a8a",
    "submenu.activeIcon.color": "#555555",
    "submenu.iconSize.width": "32px",
    "submenu.iconSize.height": "32px",

    // submenu primary color
    "submenu.backgroundColor": "transparent",
    "submenu.partition.color": "#e5e5e5",

    // submenu labels
    "submenu.normalLabel.color": "#858585",
    "submenu.normalLabel.fontWeight": "normal",
    "submenu.activeLabel.color": "#000",
    "submenu.activeLabel.fontWeight": "normal",

    // checkbox style
    "checkbox.border": "1px solid #ccc",
    "checkbox.backgroundColor": "#fff",

    // rango style
    "range.pointer.color": "#333",
    "range.bar.color": "#ccc",
    "range.subbar.color": "#606060",

    "range.disabledPointer.color": "#d3d3d3",
    "range.disabledBar.color": "rgba(85,85,85,0.06)",
    "range.disabledSubbar.color": "rgba(51,51,51,0.2)",

    "range.value.color": "#000",
    "range.value.fontWeight": "normal",
    "range.value.fontSize": "11px",
    "range.value.border": "0",
    "range.value.backgroundColor": "#f5f5f5",
    "range.title.color": "#000",
    "range.title.fontWeight": "lighter",

    // colorpicker style
    "colorpicker.button.border": "0px",
    "colorpicker.title.color": "#000",
  };
  const props = {
    includeUI: {
      loadImage: {
        path: image,
        name: "Blank",
      },
      initMenu: "filter",
      uiSize: {
        // width: "1000px",
        // height: `700px`,
        width: "100%",
        height: `${editorHeight}px`,
      },
      menuBarPosition: "bottom",
      theme: whiteTheme,
    },
    cssMaxWidth: 320,
    cssMaxHeight: 320,
    selectionStyle: {
      cornerSize: 20,
      rotatingPointOffset: 70,
    },
  };

  useEffect(() => {
    const handleResize = () => {
      setEditorHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    const imageURL = URL.createObjectURL(file);
    setImage(imageURL);
    setOriginalImage(imageURL);
  };

  function handleNextStep() {
    if (step === 1 && cropperRef.current) {
        cropperRef.current.onCropComplete();
    }

    if (step === 2) {
      blendImage();
    }
  }

  function handleBackStep() {
    let backStep = step - 1;
    if (backStep === 1) {
      setImage(originalImage);
    }
    if (backStep === 2) {
      setImage(imageCropped);
    }

    setStep(backStep);
  }

  function reset() {
    setSelectedColor("linear-gradient(180deg, rgba(96,93,93,0) 0%, rgba(255,255,255,0) 100%)");
    setImage(null);
    setImageCropped(null);
    setOriginalImage(null);
    setStep(1);
  }

  function getDir(radian, width, height) {
    radian += Math.PI;
    const HALF_WIDTH = width * 0.5;
    const HALF_HEIGHT = height * 0.5;
    const lineLength = Math.abs(width * Math.sin(radian)) + Math.abs(height * Math.cos(radian));
    const HALF_LINE_LENGTH = lineLength / 2;

    const x0 = HALF_WIDTH + Math.sin(radian) * HALF_LINE_LENGTH;
    const y0 = HALF_HEIGHT - Math.cos(radian) * HALF_LINE_LENGTH;
    const x1 = width - x0;
    const y1 = height - y0;

    return { x0, x1, y0, y1 };
  }

  const blendImage = () => {
    if (image && selectedColor) {
      const newImage = new Image();

      newImage.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = newImage.width;
        canvas.height = newImage.height;
        const context = canvas.getContext("2d");

        context.drawImage(newImage, 0, 0);
        let colorBlend = selectedColor;
        if (selectedColor.includes("gradient")) {
          const gradientMatch = selectedColor.match(/linear-gradient\((\d+)deg, (.+)\)/);
          const angle = gradientMatch[1];
          const colorStops = gradientMatch[2].split(/,\s*(?=[a-zA-Z]+\()/).map((s) => s.trim());

          let dir = getDir(angle * (Math.PI / 180), canvas.width, canvas.height);

          let gradient = context.createLinearGradient(dir.x0, dir.y0, dir.x1, dir.y1);

          colorStops.forEach((colorStop) => {
            const [color, stop] = colorStop.split(/\s(?=\d+%)/);

            gradient.addColorStop(parseFloat(stop) / 100, color);
          });

          colorBlend = gradient;
        }

        context.fillStyle = colorBlend;
        context.fillRect(0, 0, canvas.width, canvas.height);

        const a = document.createElement("a");
        const imagePath = canvas.toDataURL("image/png");
        a.href = imagePath;
        // setImage(imagePath);
        handleImageProcess(imagePath);
        // setImage(imagePath);
        // a.download = "image_with_text.png";
        // a.click();
      };

      newImage.src = image;
    }
  };

  return (
    <div>
      <Col className="user_col user_col_1_margin_top">
        <div className="unit_title_page">
          <div className="unit_title_page1">Editar/</div>
          <div className="unit_title_page2">Thumbnail</div>
        </div>
        <div className="user_button_page">
          <div style={{ display: "flex", flexDirection: "column" }}>
            {step === 1 && (
              <div>
                <label htmlFor="file-input">
                  <Button
                    component="span"
                    startIcon={<FileUploadOutlinedIcon />}
                    sx={{
                      width: "200px",
                      height: "45px",
                      borderRadius: "8px",
                      color: "#fff",
                      backgroundColor: "#1462A5",
                      "&:hover": {
                        background: "#124572",
                      },
                      boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                      textTransform: "capitalize",
                      fontSize: "16px",
                      fontWeight: "400",
                      marginBottom: "20px",
                    }}
                  >
                    Carregar Imagem
                  </Button>
                </label>
                <input id="file-input" type="file" style={{ display: "none" }} onChange={handleImageUpload} />
              </div>
            )}

            <Button
              component="span"
              startIcon={<RestartAltIcon />}
              sx={{
                width: "200px",
                height: "45px",
                borderRadius: "8px",
                color: "#fff",
                backgroundColor: "#1462A5",
                "&:hover": {
                  background: "#124572",
                },
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                textTransform: "capitalize",
                fontSize: "16px",
                fontWeight: "400",
                marginBottom: "20px",
              }}
              onClick={() => {
                reset();
              }}
            >
              Nova Imagem
            </Button>

            {(step === 2 || step === 3) && (
              <Button
                sx={{
                  width: "200px",
                  height: "45px",
                  borderRadius: "8px",
                  marginBottom: "5px",
                  color: "rgba(20, 97, 164, 1)",
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  "&:hover": {
                    background: "rgba(20, 97, 164, 1)",
                    color: "rgba(255, 255, 255, 1)",
                  },
                  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                  textTransform: "capitalize",
                  fontSize: "16px",
                  fontWeight: "400",
                }}
                startIcon={<ArrowBackIosIcon />}
                variant="outlined"
                disabled={!image}
                onClick={() => {
                  // blendImage();
                  // setStep(step + 1);
                  // handleNextStep();
                  handleBackStep();
                }}
                // onClick={() => blendImage()}
              >
                Voltar
              </Button>
            )}

            <Button
              sx={{
                width: "200px",
                height: "45px",
                borderRadius: "8px",
                color: "rgba(20, 97, 164, 1)",
                backgroundColor: "rgba(255, 255, 255, 1)",
                "&:hover": {
                  background: "rgba(20, 97, 164, 1)",
                  color: "rgba(255, 255, 255, 1)",
                },
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                textTransform: "capitalize",
                fontSize: "16px",
                fontWeight: "400",
              }}
              variant="outlined"
              endIcon={<ArrowForwardIosIcon />}
              disabled={!image || step === 3}
              onClick={() => {
                // blendImage();
                // setStep(step + 1);
                handleNextStep();
              }}
              // onClick={() => blendImage()}
            >
              Próximo passo
            </Button>
          </div>
        </div>
      </Col>
      {image && step === 1 && <Cropper ref={cropperRef} step={step} onCropFinished={handleImageProcess} imagePath={image} />}
      {image && step === 2 && (
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
          <ColorPicker
            value={selectedColor}
            hideGradientType={true}
            onChange={(color) => {
              setSelectedColor(color);
            }}
          />

          <div
            id="imageBlended"
            style={{
              position: "relative",
              marginLeft: "20px",
              height: "100%",
            }}
          >
            <img src={image} style={{}} />
            <div
              id="gradientContainer"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: selectedColor,
                // opacity: 0.5,
              }}
            />
          </div>
        </div>
      )}
      {image && step === 3 && <ImageEditor {...props} imagePath={image} selectedColor={selectedColor} />}
    </div>
  );
}

export default Thumbnails;
